import {
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RiSendInsLine, RiStopCircleLine } from '@remixicon/react';
import { Editor, Extension } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import classNames from 'classnames';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';

import { ButtonIcon, IconPlus, IconSearch } from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';
import {
  GOOGLE_PROVIDER_CONFIG,
  NOTION_PROVIDER_CONFIG,
} from '@spotify/confidence-auth0-react';
import { useAlert, useImageDropZone } from '@spotify/confidence-core-react';

import { useMentionSearch } from '../../hooks/useMentionSearch';
import { ChatAttachment, attachmentKey } from '../../types';
import { AttachContextMenu } from '../AttachContextMenu';
import { AttachmentChip, ImageAttachmentChip } from '../AttachmentChip';
import { ChatDisclaimer } from '../ChatDisclaimer';
import { SearchResourcesDialog } from '../SearchResourcesDialog';
import { ConnectBanner } from './ConnectBanner';
import {
  type GoogleDrivePasteResult,
  createGoogleDrivePastePlugin,
} from './googleDrivePastePlugin';
import { createImagePastePlugin } from './imagePastePlugin';
import {
  type NotionPasteResult,
  createNotionPastePlugin,
} from './notionPastePlugin';
import { useLinkPaste } from './useLinkPaste';

import styles from './ChatInput.module.css';

const DEFAULT_PLACEHOLDER = 'Ask to make changes, @mention resources';
const DISABLED_PLACEHOLDER = 'You do not have permission to write to this chat';

declare module '@tiptap/core' {
  interface Storage {
    markdown: MarkdownStorage;
  }
}

const getMarkdown = (editor: Editor): string => {
  return editor.storage.markdown.getMarkdown();
};

const getPlainText = (editor: Editor): string => {
  return editor
    .getText({
      textSerializers: {
        mention: () => '',
      },
    })
    .trim();
};

interface ChatInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: FormEvent, submission: ChatInputSubmission) => void;
  onInterrupt?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
  submitDisabled?: boolean;
  isRunning?: boolean;
  disableCaveat?: boolean;
  disableFocusHotkey?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  suggestion?: string | null;
  className?: string;
  chatName?: string;
  attachments?: ChatAttachment[];
  onRemoveAttachment?: (name: string) => void;
  onAttached?: (attachment: ChatAttachment) => void;
  onImageFile?: (file: File) => void;
  composerContext?: React.ReactNode;
  composerDecoration?: React.ReactNode;
  initialMention?: ChatInputMention;
  toolbarStart?: React.ReactNode;
  density?: 'regular' | 'compact';
}

type ChatInputMention = {
  id: string;
  label: string;
};

type ChatInputSubmission = {
  plainText: string;
};

export const ChatInput = ({
  value,
  onChange,
  onSubmit,
  onInterrupt,
  autoFocus = false,
  disabled = false,
  submitDisabled = false,
  isRunning = false,
  disableCaveat = false,
  disableFocusHotkey = false,
  placeholder,
  'aria-label': ariaLabel,
  suggestion,
  className,
  chatName,
  attachments,
  onRemoveAttachment,
  onAttached,
  onImageFile,
  composerContext,
  composerDecoration,
  initialMention,
  toolbarStart,
  density = 'regular',
}: ChatInputProps) => {
  const isInternalChange = useRef(false);

  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  const googlePaste = useLinkPaste<GoogleDrivePasteResult>({
    providerConfig: GOOGLE_PROVIDER_CONFIG,
    sourceType: r => r.sourceType,
    extractSourceId: r => r.fileId,
    providerLabel: 'Google',
    duplicateLabel: 'document',
    attachments,
    onAttached,
  });

  const notionPaste = useLinkPaste<NotionPasteResult>({
    providerConfig: NOTION_PROVIDER_CONFIG,
    sourceType: 'NOTION',
    extractSourceId: r => r.pageId,
    providerLabel: 'Notion',
    duplicateLabel: 'Notion page',
    attachments,
    onAttached,
  });

  const googleDrivePastePlugin = useMemo(
    () => createGoogleDrivePastePlugin(googlePaste.onPastedRef),
    [googlePaste.onPastedRef],
  );

  const notionPastePlugin = useMemo(
    () => createNotionPastePlugin(notionPaste.onPastedRef),
    [notionPaste.onPastedRef],
  );

  const onImageFileRef = useRef<((file: File) => void) | null>(null);
  onImageFileRef.current = onImageFile ?? null;

  const imagePastePlugin = useMemo(
    () => createImagePastePlugin(onImageFileRef),
    [],
  );

  const alertApi = useAlert();
  const { isDragOver, dragHandlers } = useImageDropZone(
    file => {
      if (!disabled) onImageFile?.(file);
    },
    {
      onReject: () => {
        alertApi.post({
          severity: 'error',
          message: 'Only image files are supported.',
        });
      },
    },
  );

  const imageDragHandlers = onImageFile ? dragHandlers : undefined;

  const { mentionExtension, mentionOpenRef } = useMentionSearch();
  const editorRef = useRef<Editor | null>(null);
  const insertedInitialMentionRef = useRef<string | null>(null);

  const handleSearchResourceSelect = useCallback(
    (resource: { id: string; label: string }) => {
      const ed = editorRef.current;
      if (!ed) return;
      ed.chain()
        .focus()
        .insertContent({
          type: 'mention',
          attrs: { id: resource.id, label: resource.label },
        })
        .insertContent(' ')
        .run();
      setSearchDialogOpen(false);
    },
    [],
  );

  // Mirror the latest attachment count in a ref so the (once-memoised)
  // submitOnEnter extension can decide whether Enter should send when there
  // is no text but attachments are present.
  const hasAttachmentsRef = useRef(false);
  hasAttachmentsRef.current = (attachments?.length ?? 0) > 0;

  const submitDisabledRef = useRef(submitDisabled);
  submitDisabledRef.current = submitDisabled;

  const doSubmitRef = useRef(() => {});
  doSubmitRef.current = () => {
    if (disabledRef.current || submitDisabledRef.current) return;
    const ed = editorRef.current;
    const plainText = ed ? getPlainText(ed) : '';
    const markdown = ed ? getMarkdown(ed).trim() : '';
    if (markdown || hasAttachmentsRef.current) {
      isInternalChange.current = false;
      onSubmitRef.current(new Event('submit') as unknown as FormEvent, {
        plainText,
      });
      ed?.commands.focus();
    }
  };

  // Submit-on-Enter as a low-priority ProseMirror plugin so the mention
  // suggestion plugin processes Enter first.
  const submitOnEnter = useMemo(
    () =>
      Extension.create({
        name: 'submitOnEnter',
        priority: 200,
        addProseMirrorPlugins() {
          return [
            new Plugin({
              key: new PluginKey('submitOnEnter'),
              props: {
                handleKeyDown: (_view, event) => {
                  if (
                    event.key === 'Enter' &&
                    !event.shiftKey &&
                    !mentionOpenRef.current
                  ) {
                    event.preventDefault();
                    doSubmitRef.current();
                    return true;
                  }
                  return false;
                },
              },
            }),
          ];
        },
      }),
    [mentionOpenRef],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bold: false,
        italic: false,
        strike: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      Placeholder.configure({
        placeholder: placeholder ?? DEFAULT_PLACEHOLDER,
      }),
      mentionExtension,
      submitOnEnter,
      googleDrivePastePlugin,
      notionPastePlugin,
      imagePastePlugin,
    ],
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
    content: value,
    editable: !disabled,
    autofocus: autoFocus ? 'end' : false,
  });

  editorRef.current = editor;

  useEffect(() => {
    if (!editor) {
      return undefined;
    }

    const handleUpdate = ({ editor: currentEditor }: { editor: Editor }) => {
      isInternalChange.current = true;
      const md = getMarkdown(currentEditor);
      // Synthesize a change event to keep the same interface
      const syntheticEvent = {
        target: { value: md },
      } as React.ChangeEvent<HTMLTextAreaElement>;
      onChangeRef.current(syntheticEvent);
    };

    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor]);

  // Sync external value changes
  useEffect(() => {
    if (!editor) return;
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    const currentMarkdown = getMarkdown(editor);
    if (value !== currentMarkdown) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  const initialMentionId = initialMention?.id;
  const initialMentionLabel = initialMention?.label;
  useEffect(() => {
    if (!editor || !initialMentionId || initialMentionLabel === undefined)
      return undefined;

    const insertInitialMention = (): void => {
      if (editor.isDestroyed || getMarkdown(editor).trim()) return;

      const mentionKey = `${initialMentionId}:${initialMentionLabel}`;
      if (insertedInitialMentionRef.current === mentionKey) return;
      editor
        .chain()
        .insertContent({
          type: 'mention',
          attrs: { id: initialMentionId, label: initialMentionLabel },
        })
        .insertContent(' ')
        .focus('end')
        .run();
      insertedInitialMentionRef.current = mentionKey;
    };

    if (editor.isInitialized) insertInitialMention();
    editor.on('create', insertInitialMention);
    return () => {
      editor.off('create', insertInitialMention);
    };
  }, [editor, initialMentionId, initialMentionLabel]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled, false);
    }
  }, [editor, disabled]);

  // Meta+L hotkey to focus
  const handleGlobalKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.metaKey && e.key === 'l' && !disabledRef.current) {
        e.preventDefault();
        editor?.commands.focus();
      }
    },
    [editor],
  );

  useEffect(() => {
    if (disableFocusHotkey) return undefined;
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [disableFocusHotkey, handleGlobalKeyDown]);

  const hasAttachments = (attachments?.length ?? 0) > 0;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    doSubmitRef.current();
  };

  const isEmpty =
    useEditorState({
      editor,
      selector: ({ editor: currentEditor }) =>
        !currentEditor || getMarkdown(currentEditor).trim().length === 0,
    }) ?? true;
  const canSubmit = !isEmpty || hasAttachments;

  const handleContainerMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (!editor || disabled || event.button !== 0) return;
    const target = event.target as HTMLElement;
    const isBlankArea = target.dataset.focusEditor !== undefined;
    if (!isBlankArea) return;
    event.preventDefault();
    if (editor.isFocused) return;
    editor.commands.focus('end');
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={classNames(styles.root, className)}
      data-track-source-anchor="9ae759975d;chat-input-form" {...imageDragHandlers}
    >
      <div
        className={styles.inputContainer}
        data-bg="neutral-2"
        data-focus-editor
        data-disabled={disabled || undefined}
        data-testid="composer-container"
        role="presentation"
        onMouseDown={handleContainerMouseDown}
        style={
          {
            backgroundColor: disabled
              ? 'var(--background-tinted-base)'
              : 'var(--background-elevated-highlight)',
            '--hotkey-hint':
              disableFocusHotkey ||
              disabled ||
              googlePaste.showBanner ||
              notionPaste.showBanner
                ? 'none'
                : '"⌘L to focus"',
          } as React.CSSProperties
        }
      >
        {composerDecoration}
        {isDragOver && onImageFile && <div className={styles.dropOverlay} />}
        {(composerContext || hasAttachments) && (
          <div className={styles.attachments}>
            {composerContext}
            {attachments?.map(attachment =>
              attachment.kind === 'image' ||
              attachment.kind === 'uploading-image' ? (
                <ImageAttachmentChip
                  key={attachmentKey(attachment)}
                  attachment={attachment}
                  onRemove={onRemoveAttachment}
                />
              ) : (
                <AttachmentChip
                  key={attachmentKey(attachment)}
                  attachment={attachment}
                  onRemove={onRemoveAttachment}
                />
              ),
            )}
          </div>
        )}
        {googlePaste.showBanner && (
          <ConnectBanner
            providerLabel="Google"
            resourceLabel="Google Doc"
            connectionError={googlePaste.connectionError}
            isConnecting={googlePaste.isConnecting}
            onConnect={googlePaste.handleConnect}
          />
        )}
        {notionPaste.showBanner && (
          <ConnectBanner
            providerLabel="Notion"
            resourceLabel="Notion page"
            connectionError={notionPaste.connectionError}
            isConnecting={notionPaste.isConnecting}
            onConnect={notionPaste.handleConnect}
          />
        )}
        <div className={styles.editorWrapper} data-focus-editor>
          {isEmpty && suggestion && (
            <div className={styles.suggestion}>
              {suggestion}
              <span className={styles.tabHint}>Tab</span>
            </div>
          )}
          <div
            className={styles.editorRow}
            data-testid="composer-editor-row"
            data-focus-editor
          >
            <EditorContent editor={editor} className={styles.editorContent} />
          </div>
        </div>
        <div
          className={classNames(styles.toolbarCollapsible, {
            [styles.toolbarCompact]: density === 'compact',
          })}
          data-focus-editor
          aria-hidden={density === 'compact' || undefined}
        >
          <div className={styles.toolbarContent} data-focus-editor>
            <div
              className={styles.toolbar}
              data-focus-editor
              data-testid="composer-toolbar"
            >
              {onAttached ? (
                <AttachContextMenu
                  onAttached={onAttached}
                  chatName={chatName}
                  isDisabled={disabled}
                  onImageFile={onImageFile}
                />
              ) : (
                <ButtonIcon
                  aria-label="Add context"
                  variant="secondary"
                  isVisuallyDisabled
                  aria-disabled="true"
                >
                  <IconPlus />
                </ButtonIcon>
              )}
              <ButtonIcon
                aria-label="Search resources"
                variant="secondary"
                disabled={disabled}
                onClick={() => setSearchDialogOpen(true)}
               data-track-source-anchor="18c74f568f;;low">
                <IconSearch />
              </ButtonIcon>
              {toolbarStart && <div>{toolbarStart}</div>}
              <div className={styles.spacer} data-focus-editor />
              {isRunning ? (
                <ButtonIcon
                  aria-label="Stop"
                  variant="secondary"
                  onClick={onInterrupt}
                  className={styles.stopButton}
                 data-track-source-anchor="54b90304c7;;low">
                  <RiStopCircleLine size={16} />
                </ButtonIcon>
              ) : (
                <TooltipRoot>
                  <ButtonIcon
                    aria-label="Send message"
                    type="submit"
                    variant={canSubmit ? 'primary' : 'secondary'}
                    isVisuallyDisabled={
                      disabled || submitDisabled || !canSubmit
                    }
                    aria-disabled={
                      disabled || submitDisabled || !canSubmit || undefined
                    }
                    data-track-id="button.send-message"
                    data-track-action="create"
                  >
                    <RiSendInsLine size={16} />
                  </ButtonIcon>
                  {disabled && <Tooltip>{DISABLED_PLACEHOLDER}</Tooltip>}
                </TooltipRoot>
              )}
            </div>
          </div>
        </div>
      </div>
      {!disableCaveat && <ChatDisclaimer />}
      <SearchResourcesDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        onSelect={handleSearchResourceSelect}
       data-track-source-anchor="abec45871e;chat-input-search-resources-dialog"/>
    </form>
  );
};
